import { RootState } from 'redux/schemas';
import { createSelector } from 'reselect';
import {
  CompletionStatus,
  MentoringProgramRole,
} from 'redux/schemas/models/mentoring-program-enrollments';
import {
  MentoringProgramGuideRequirementType,
  SessionSequenceCriteria,
  ProfileCompletionCriteria,
  MentoringProgramMilestone,
  MentoringProgramGuide,
  ConnectionSessionCriteria,
} from 'redux/schemas/models/mentoring-program-guides';
import { getMentorshipProgramSessionIndex } from 'redux/selectors/mentorship-program-sessions';
import { TargetAudience } from 'redux/schemas/models/mentoring-programs';


export const getMentoringProgramGuides = (
  state: RootState,
) => state.models.mentorshipProgramGuides;

export const getMentoringProgramGuidesList = createSelector(
  (state: RootState) => state.models.mentorshipProgramGuides,
  (state, ids: number[]) => ids,
  (mentorshipProgramGuides: Record<number, MentoringProgramGuide>, ids: number[]) => ids.map((id) => mentorshipProgramGuides[id]),
);

export const getMentoringProgramGuideById = (
  guideId: number | null,
) => (state: RootState) => (
  (guideId && state.models.mentorshipProgramGuides[guideId]) || null
);

const byRole = (role: MentoringProgramRole) => (guide: MentoringProgramGuide) => (
  guide.role === TargetAudience.MENTOR_AND_MENTEE
  || (guide.role === TargetAudience.MENTOR
      && role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR)
  || (guide.role === TargetAudience.MENTEE
      && role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE)
);

const bySessionOrConnectionSessionSequence = (
  sessionIndex: number,
) => (guide: MentoringProgramGuide) => {
  const req = guide.mentorshipProgramGuideRequirement;
  const { sequence } = req.criteria as SessionSequenceCriteria;
  const { upperLimit } = req.criteria as ConnectionSessionCriteria;
  if (sequence) {
    return (
      req.type === MentoringProgramGuideRequirementType.SESSION
      && Array.isArray(sequence)
      && sequence.includes(sessionIndex)
    );
  }
  return (
    !!upperLimit
    && req.type === MentoringProgramGuideRequirementType.CONNECTION_SESSION
    && upperLimit === sessionIndex
  );
};

const byProfileCompletionOrReviewMilestone = (
  completionStatus: CompletionStatus,
) => (guide: MentoringProgramGuide) => {
  const req = guide.mentorshipProgramGuideRequirement;
  const { state, status } = req.criteria as ProfileCompletionCriteria;
  return (
    req.type === MentoringProgramGuideRequirementType.MILESTONE
    && (state === MentoringProgramMilestone.PROFILE_REVIEW || state === MentoringProgramMilestone.PROFILE_COMPLETION)
    && status === completionStatus
  );
};

export const selectGuidesForSessionByRole = (
  role: MentoringProgramRole,
  sessionId: number,
) => (state: RootState) => {
  const sessionIndex = getMentorshipProgramSessionIndex(sessionId)(state);
  if (!sessionIndex) return [];
  const guides = Object.values(state.models.mentorshipProgramGuides);
  return guides.filter(byRole(role)).filter(bySessionOrConnectionSessionSequence(sessionIndex));
};

export const selectGuidesForProfileCompletionOrReviewMilestoneByRole = (
  role: MentoringProgramRole,
  completionStatus: CompletionStatus,
) => (state: RootState) => (
  Object.values(state.models.mentorshipProgramGuides)
    .filter(byRole(role))
    .filter(byProfileCompletionOrReviewMilestone(completionStatus))
);

export const selectGuidesForMyGoalsByRole = (
  role: MentoringProgramRole,
) => (state: RootState) => (
  Object.values(state.models.mentorshipProgramGuides)
    .filter(byRole(role))
    .filter((guide) => guide.mentorshipProgramGuideRequirement.type === MentoringProgramGuideRequirementType.EVENT)
);
